import { type FC, useState, useEffect, useMemo, useCallback, useContext, useRef } from 'react';
import { Box, Typography, Drawer, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { IMATEntry } from '../../types/mat-types';
import useMatDiscoveryByKey from './useMatDiscoveryByKey';
import useMatFieldsInfo from './hooks/useMatFieldsInfo';
import MATDiscoveryTableHorizontal from './MATDiscoveryTableHorizontal';
import ButtonGlynt from '../common/Buttons/ButtonGlynt';
import { btnSubtleNeutralSx } from '../common/filterStyles';
import {
  coerce,
  normalizeKey,
  getExistingValueForCol,
  getFirstDiscoveredValueForCol,
  getOptions,
} from './matDiscoveryTableHelpers';
import { AppContext } from '../../App';
import useGlynt from '../hooks/useGlynt';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  MATRowDetailDrawerProps,
  MATRowWithDiscoveredFlag,
  MATCreateBody,
} from './MATRowDetailDrawer.types';
import {
  validateDraft,
  getSuccessMessage,
  getSubmitButtonText,
  sanitizeMatRequestBody,
  selectDiscoveryItemForKey,
} from './MATRowDetailDrawer.utils';

// Query keys for React Query cache invalidation
const MAT_QUERY_KEYS = [
  'mat-discovery-by-key',
  'mat-discovery',
  'mat-discovery-all-keys',
  'mat-discovery-full',
  'mat',
] as const;

const DRAG_HANDLE_HEIGHT = '16px';
const DRAWER_BORDER_RADIUS = '8px';
const DRAWER_BACKGROUND_COLOR = '#fafafa';
const DRAWER_BORDER_COLOR = '#e0e0e0';
const MIN_DRAWER_HEIGHT_PERCENT = 20;
const MAX_DRAWER_HEIGHT_PERCENT = 80;

const MATRowDetailDrawer: FC<MATRowDetailDrawerProps> = ({ open, data, onClose, onSuccess, cFieldKeys = [] }) => {
  const { org, setToast } = useContext(AppContext);
  const matKey = data?.MatKey;

  // Memoize computed values for better performance
  const isDiscoveredRow = useMemo(
    () => !!(data as MATRowWithDiscoveredFlag)?.__isDiscovered__,
    [data]
  );

  const isMissingStdVendorName = useMemo(() => {
    if (!isDiscoveredRow || !data) return false;
    const stdVendorName = (data as any)['std-VendorName'];
    return stdVendorName == null || stdVendorName === '';
  }, [isDiscoveredRow, data]);

  const isMissingClient = useMemo(() => {
    if (!isDiscoveredRow || !data) return false;
    const clientName = (data as any).ClientName;
    const clientAbbr = (data as any).ClientAbbr;
    return (clientName == null || clientName === '') && (clientAbbr == null || clientAbbr === '');
  }, [isDiscoveredRow, data]);

  // Drawer height state (null means auto, number means explicit vh)
  const [drawerHeight, setDrawerHeight] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Reset height to auto when drawer closes
  useEffect(() => {
    if (!open) {
      setDrawerHeight(null);
    }
  }, [open]);

  // Handle resize drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const viewportHeight = window.innerHeight;
      const newHeight = ((viewportHeight - e.clientY) / viewportHeight) * 100;
      // Clamp between min and max percentages
      const clampedHeight = Math.max(
        MIN_DRAWER_HEIGHT_PERCENT,
        Math.min(MAX_DRAWER_HEIGHT_PERCENT, newHeight)
      );
      setDrawerHeight(clampedHeight);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    // Set cursor on body during drag
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Fetch discovered rows for this mat_key
  const {
    data: discoveryData,
    isLoading: isDiscoveryLoading,
    isError: isDiscoveryError,
  } = useMatDiscoveryByKey({
    matKey,
    enabled: open && !!matKey,
  });

  const current = useMemo(
    () => selectDiscoveryItemForKey(discoveryData?.results, matKey),
    [discoveryData, matKey]
  );

  const hasNoDiscoveryMatch = useMemo(
    () => isDiscoveredRow && !isDiscoveryLoading && !isDiscoveryError && !current,
    [isDiscoveredRow, isDiscoveryLoading, isDiscoveryError, current]
  );

  // Fields info (read-only metadata)
  const {
    data: fieldsInfo,
    isLoading: isFieldsInfoLoading,
    isError: isFieldsInfoError,
    refetch: refetchFieldsInfo,
  } = useMatFieldsInfo();

  // Fields that are always read-only
  const alwaysReadOnlySet = useMemo(() => {
    if (!fieldsInfo) return new Set<string>();
    return new Set<string>((fieldsInfo.read_only_fields || []).map((k) => normalizeKey(k)));
  }, [fieldsInfo]);

  // Fields that can only be set during creation (POST), not during update (PATCH)
  const postOnlySet = useMemo(() => {
    if (!fieldsInfo) return new Set<string>();
    return new Set<string>((fieldsInfo.post_only_fields || []).map((k) => normalizeKey(k)));
  }, [fieldsInfo]);

  // Combined read-only set based on whether we're creating or updating
  const readOnlySet = useMemo(() => {
    const combined = new Set(alwaysReadOnlySet);
    // When updating existing MAT rows, post_only fields are also read-only
    if (!isDiscoveredRow) {
      postOnlySet.forEach((field) => combined.add(field));
    }
    return combined;
  }, [alwaysReadOnlySet, postOnlySet, isDiscoveredRow]);

  const requiredSet = useMemo(() => {
    if (!fieldsInfo) return new Set<string>();
    return new Set<string>(fieldsInfo.required_fields || []);
  }, [fieldsInfo]);

  const [requiredErrors, setRequiredErrors] = useState<Set<string>>(new Set());

  const blockEditing = useMemo(
    () => isFieldsInfoLoading || isFieldsInfoError,
    [isFieldsInfoLoading, isFieldsInfoError]
  );

  // Build columns from discovered rows and existing MAT entry
  // Preserve order from API response (same as grid)
  const columns = useMemo(() => {
    if (!data) return [];

    const seen = new Set<string>();
    const orderedColumns: string[] = [];

    // Use the clicked row's key order (works for both discovered and existing MAT rows)
    // Filter out internal columns (__ prefix) used for grid markers
    Object.keys(data).forEach((k) => {
      if (k.startsWith('__')) return;
      if (!seen.has(k)) {
        seen.add(k);
        orderedColumns.push(k);
      }
    });

    // Add any additional columns from discovered rows that aren't in the clicked row
    if (current?.discovered_rows) {
      current.discovered_rows.forEach((r: any) => {
        const d = r?.data || {};
        Object.keys(d).forEach((k) => {
          if (!seen.has(k)) {
            seen.add(k);
            orderedColumns.push(k);
          }
        });
      });
    }

    // Add DataSourceStatus if we have discovery data (it's at the discovery item level)
    // But only for existing MAT rows, not discovered rows
    if (!isDiscoveredRow && current?.data_source_status && !seen.has('DataSourceStatus')) {
      orderedColumns.push('DataSourceStatus');
    }

    // Append C-Field keys from existing MAT entries that aren't already present
    // This ensures C-Fields are available when creating from discovered rows
    // and are visible when editing existing rows
    cFieldKeys.forEach((k) => {
      if (!seen.has(k)) {
        seen.add(k);
        orderedColumns.push(k);
      }
    });

    return orderedColumns;
  }, [current, data, cFieldKeys]);

  // Draft state for the "Build Row"
  const [draft, setDraft] = useState<Record<string, any>>({});

  // Track the data we initialized from to detect when we need to re-initialize
  const initializedFromData = useRef<any>(null);

  // Initialize draft from existing MAT data or discovered data
  // Only initialize if we haven't initialized from this specific data yet
  useEffect(() => {
    if (!data || !open) {
      // Reset when drawer closes
      initializedFromData.current = null;
      setDraft({});
      return;
    }

    // For discovered rows, wait until we have discovered_rows data with actual values
    if (isDiscoveredRow) {
      const hasDiscoveredData = current?.discovered_rows && current.discovered_rows.length > 0;
      if (!hasDiscoveredData) {
        // Settled with no entry for this key: drop whatever the previous key left behind
        if (!isDiscoveryLoading) {
          setDraft((prev) => (Object.keys(prev).length === 0 ? prev : {}));
          initializedFromData.current = null;
        }
        return;
      }
    }

    // Check if we've already initialized from this exact data
    // For discovered rows, check current; for existing rows, check data
    const sourceData = isDiscoveredRow ? current : data;
    if (initializedFromData.current === sourceData) return;

    const next: Record<string, any> = {};

    // For discovered rows, only use discovered data
    // For existing MAT rows, ONLY use existing values
    columns.forEach((col) => {
      if (!isDiscoveredRow) {
        // Existing MAT row: use ONLY existing value, no fallback to discovered
        const fromExisting = getExistingValueForCol(col, data);
        if (fromExisting != null) next[col] = coerce(fromExisting);
        else next[col] = '';
      } else {
        // Discovered row: only use discovered data
        const fromDiscovered = getFirstDiscoveredValueForCol(col, current);
        if (fromDiscovered != null) next[col] = coerce(fromDiscovered);
        else next[col] = '';
      }
    });

    setDraft(next);
    initializedFromData.current = sourceData;
  }, [columns, current, data, open, isDiscoveredRow, isDiscoveryLoading]);

  const setField = useCallback(
    (key: string, value: any) => {
      if (readOnlySet.has(normalizeKey(key))) return;
      if (blockEditing) return;
      setDraft((prev) => ({ ...prev, [key]: value }));
    },
    [readOnlySet, blockEditing]
  );

  const handleCopyFromRow = useCallback(
    (source: Record<string, any>) => {
      setDraft((prev) => {
        const writableFields: Record<string, any> = {};
        columns.forEach((col) => {
          const normCol = normalizeKey(col);
          if (!readOnlySet.has(normCol) && normCol !== normalizeKey('MatKey')) {
            if (source[col] !== undefined) {
              writableFields[col] = source[col];
            }
          }
        });
        return { ...prev, ...writableFields };
      });
    },
    [columns, readOnlySet]
  );

  const handleGetOptions = useCallback(
    (key: string): string[] => {
      // For discovered rows, don't pass the row as existing MAT data
      const existingMatData = isDiscoveredRow ? null : data;
      return getOptions(key, existingMatData, current);
    },
    [isDiscoveredRow, data, current]
  );

  // Submit handling: create or update MAT with current draft
  const glynt = useGlynt();
  const queryClient = useQueryClient();

  const createMut = useMutation({
    mutationFn: async (body: MATCreateBody) => {
      const g = await glynt;
      if (!org?.id) throw new Error('Missing orgId');
      return g.mat.create(org.id, body);
    },

    onSuccess: async () => {
      // When creating, invalidate discovery queries (to remove the discovered row)
      // and MAT queries (to show the new entry)
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['mat-discovery-by-key']
        }),
        queryClient.invalidateQueries({
          queryKey: ['mat-discovery']
        }),
        queryClient.invalidateQueries({
          queryKey: ['mat-discovery-all-keys']
        }),
        queryClient.invalidateQueries({
          queryKey: ['mat-discovery-full']
        }),
        queryClient.invalidateQueries({
          queryKey: ['mat-discovery-keys']
        }),
        queryClient.invalidateQueries({
          queryKey: ['mat']
        }),
        queryClient.invalidateQueries({
          queryKey: ['mat-cfield-keys']
        }),
      ]);
    }
  });

  const updateMut = useMutation({
    mutationFn: async (body: MATCreateBody) => {
      const g = await glynt;
      if (!org?.id) throw new Error('Missing orgId');
      const matRowIndex = data?.MatRowIndex;
      if (matRowIndex == null) throw new Error('Missing MatRowIndex for MAT update');
      return g.mat.update(org.id, matRowIndex, body);
    },

    onSuccess: async () => {
      // When updating, invalidate MAT queries to show the updated entry
      // Also invalidate discovery keys in case the update affects discovered data
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['mat-discovery-keys']
        }),
        queryClient.invalidateQueries({
          queryKey: ['mat']
        }),
        queryClient.invalidateQueries({
          queryKey: ['mat-cfield-keys']
        }),
      ]);
    }
  });

  const onSubmit = async () => {
    // Validate draft using helper function
    const validation = validateDraft(draft, requiredSet);

    if (!validation.isValid) {
      setRequiredErrors(new Set(validation.missingFields));
      setToast({ open: true, variant: 'error', message: validation.errorMessage! });
      return;
    }

    setRequiredErrors(new Set());

    try {
      // Prepare request body
      const requestBody = sanitizeMatRequestBody(draft, {
        stripDataSourceStatus: isDiscoveredRow,
      }) as MATCreateBody;

      // Use create mutation for discovered rows, update mutation for existing MAT rows
      const mutation = isDiscoveredRow ? createMut : updateMut;
      await mutation.mutateAsync(requestBody);
      const successMsg = getSuccessMessage(isDiscoveredRow);
      setToast({ open: true, variant: 'success', message: successMsg });
      onSuccess?.();
      onClose();
    } catch (error: unknown) {
      const action = isDiscoveredRow ? 'create' : 'update';
      const msg = error instanceof Error ? error.message : 'Unknown error';
      setToast({
        open: true,
        variant: 'error',
        message: `Failed to ${action} MAT entry: ${msg}`,
      });
    }
  };

  if (!data) return null;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      data-cy="mat-row-detail-drawer"
      PaperProps={{
        sx: {
          height: drawerHeight !== null ? `${drawerHeight}vh` : 'auto',
          minHeight: '25vh',
          maxHeight: '80vh',
          borderTopLeftRadius: DRAWER_BORDER_RADIUS,
          borderTopRightRadius: DRAWER_BORDER_RADIUS,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
          transition: isDragging ? 'none' : 'height 0.2s ease',
        },
      }}
      SlideProps={{
        timeout: 300,
      }}
    >
      {/* Drag Handle */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          height: DRAG_HANDLE_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'ns-resize',
          backgroundColor: DRAWER_BACKGROUND_COLOR,
          borderTopLeftRadius: DRAWER_BORDER_RADIUS,
          borderTopRightRadius: DRAWER_BORDER_RADIUS,
          borderBottom: `1px solid ${DRAWER_BORDER_COLOR}`,
          '&:hover': {
            backgroundColor: '#eeeeee',
          },
          '&:active': {
            backgroundColor: '#e0e0e0',
          },
          userSelect: 'none',
        }}
      >
        <Box
          sx={{
            width: '40px',
            height: '3px',
            borderRadius: '2px',
            backgroundColor: '#bdbdbd',
          }}
        />
      </Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1.5,
          py: 0.5,
          borderBottom: `1px solid ${DRAWER_BORDER_COLOR}`,
          backgroundColor: DRAWER_BACKGROUND_COLOR,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
          {isDiscoveredRow ? 'Discovered Entry' : 'Existing MAT Row'}: {matKey || 'N/A'}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ p: 0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {/* Content */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 1.5, pt: 1, pb: 0 }}>
          {isFieldsInfoLoading && (
            <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.7 }}>
              Loading field editability…
            </Typography>
          )}
          {isFieldsInfoError && (
            <Box sx={{ mb: 1 }}>
              <Typography color="error" variant="body2" component="span" sx={{ mr: 1 }}>
                Failed to load field editability metadata.
              </Typography>
              <ButtonGlynt
                variant="outlined"
                sx={btnSubtleNeutralSx}
                onClick={() => refetchFieldsInfo()}
              >
                Retry
              </ButtonGlynt>
            </Box>
          )}
        </Box>

        {isMissingStdVendorName && (
          <Box
            sx={{
              mx: 1.5,
              mt: 1,
              mb: 1.5,
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderRadius: 1,
              bgcolor: '#FFF3E0',
              border: '1px solid #FFB74D',
            }}
            data-cy="warning-missing-std-vendor-name"
          >
            <WarningAmberIcon sx={{ color: '#E65100', fontSize: 28 }} />
            <Typography variant="body2" sx={{ color: '#E65100' }}>
              Vendor name not found. Add vendor first.
            </Typography>
          </Box>
        )}

        {isMissingClient && (
          <Box
            sx={{
              mx: 1.5,
              mt: isMissingStdVendorName ? 0 : 1,
              mb: 1.5,
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderRadius: 1,
              bgcolor: '#FFF3E0',
              border: '1px solid #FFB74D',
            }}
            data-cy="warning-missing-client"
          >
            <WarningAmberIcon sx={{ color: '#E65100', fontSize: 28 }} />
            <Typography variant="body2" sx={{ color: '#E65100' }}>
              Client not set. Set client before creating MAT entry.
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: 1.5,
            pb: 1,
            '& .MuiPaper-root': { overflow: 'auto' },
          }}
        >
          {isDiscoveryError ? (
            <Typography color="error">Failed to load discovery data.</Typography>
          ) : (
            <MATDiscoveryTableHorizontal
              columns={columns}
              requiredSet={requiredSet}
              readOnlySet={readOnlySet}
              requiredErrors={requiredErrors}
              draft={draft}
              setField={setField}
              blockEditing={blockEditing}
              existingMat={isDiscoveredRow ? null : data}
              current={current}
              copyFromRow={handleCopyFromRow}
              loadingRow={isDiscoveryLoading}
              coerce={coerce}
              getOptions={handleGetOptions}
            />
          )}
        </Box>
      </Box>
      {/* Footer - Fixed at bottom */}
      {!isDiscoveryError && (
        <Box
          sx={{
            borderTop: `1px solid ${DRAWER_BORDER_COLOR}`,
            backgroundColor: DRAWER_BACKGROUND_COLOR,
            px: 1.5,
            py: 1,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Tooltip
            title={
              hasNoDiscoveryMatch
                ? 'No discovered rows found for this key.'
                : isMissingStdVendorName
                  ? 'Vendor name not found. Add vendor first.'
                  : isMissingClient
                    ? 'Client not set. Set client before creating MAT entry.'
                    : ''
            }
            placement="top"
          >
            <span>
              <ButtonGlynt
                variant="contained"
                color="primary"
                onClick={onSubmit}
                disabled={
                  !org?.id ||
                  createMut.isPending ||
                  updateMut.isPending ||
                  blockEditing ||
                  hasNoDiscoveryMatch ||
                  isMissingStdVendorName ||
                  isMissingClient
                }
                data-cy="btn-submit-mat-row"
              >
                {getSubmitButtonText(
                  createMut.isPending || updateMut.isPending,
                  blockEditing,
                  isDiscoveredRow
                )}
              </ButtonGlynt>
            </span>
          </Tooltip>
        </Box>
      )}
    </Drawer>
  );
};

export default MATRowDetailDrawer;
